<template>
    <a-modal
        v-model:open="visible"
        :closable="false"
        :mask-closable="false"
        :keyboard="false"
        :footer="null"
        title="⚠️ Khôi phục bản ghi chưa lưu"
        width="440px"
    >
        <div class="rec-recovery">
            <p class="rr-warn">
                <template v-if="meta?.status === 'recording'">
                    Quá trình ghi trước đó đã <b>bị dừng đột ngột</b> — có thể do tab bị đóng, hoặc
                    trình duyệt tự giải phóng bộ nhớ vì <b>ghi quá lâu</b>. Bản ghi đã được lưu tạm
                    trên máy bạn; hãy tải về ngay để không bị mất.
                </template>
                <template v-else>
                    Bạn có một bản ghi <b>chưa được tải lên</b>. Hãy tải về hoặc tải lên hệ thống.
                </template>
            </p>

            <div class="rr-info">
                <div class="rr-row"><span>Loại</span><b>{{ meta?.type === 'screen' ? 'Ghi màn hình' : 'Ghi âm' }}</b></div>
                <div class="rr-row"><span>Tên file</span><b class="rr-file" :title="meta?.filename">{{ meta?.filename }}</b></div>
                <div class="rr-row" v-if="durationLabel"><span>Thời lượng</span><b>{{ durationLabel }}</b></div>
            </div>

            <div class="rr-actions">
                <a-button type="primary" :loading="downloading" @click="onDownload">
                    <template #icon><DownloadOutlined /></template>
                    Tải về máy
                </a-button>
                <a-button :loading="uploading" @click="onUpload">
                    <template #icon><CloudUploadOutlined /></template>
                    Tải lên hệ thống
                </a-button>
                <a-button danger type="text" :disabled="downloading || uploading" @click="onDiscard">
                    Xoá bản ghi
                </a-button>
            </div>
        </div>
    </a-modal>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, watch} from 'vue'
import {notification, Modal} from 'ant-design-vue'
import {DownloadOutlined, CloudUploadOutlined} from '@ant-design/icons-vue'
import {useAuthStore} from '@/stores'
import {getSessionMeta, getChunkCount, assembleBlob, clearSession, RecorderSessionMeta} from '@/utils/recorderStore'
import TaskService from '@/services/task'

const visible = ref(false)
const meta = ref<RecorderSessionMeta | null>(null)
const downloading = ref(false)
const uploading = ref(false)

const authStore = useAuthStore()

const durationLabel = computed(() => {
    const s = meta.value?.durationSec
    if (!s) return ''
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    const pad = (n: number) => n.toString().padStart(2, '0')
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`
})

/**
 * Check IndexedDB for any pending recording and show the recovery modal.
 * Called on mount and again whenever the user successfully re-authenticates,
 * so that a recording captured before a session expiry can still be uploaded.
 */
const checkAndShow = async () => {
    try {
        const m = await getSessionMeta()
        if (!m) return
        const count = await getChunkCount()
        if (count > 0) {
            meta.value = m
            visible.value = true
        } else {
            // No data — clear stale metadata.
            await clearSession()
        }
    } catch (e) {
        console.warn('[RecordingRecovery] check failed:', e)
    }
}

onMounted(checkAndShow)

// Re-check when the user logs back in after a TOKEN_INVALID dismissal.
// isLoggedIn goes from false → true when AuthStore restores a new token.
watch(
    () => authStore.isLoggedIn,
    (loggedIn) => {
        if (loggedIn && !visible.value) {
            checkAndShow()
        }
    }
)

const buildBlob = async (): Promise<Blob | null> => {
    if (!meta.value) return null
    return assembleBlob(meta.value.mimeType)
}

/**
 * Bản dùng để GỬI LÊN: ưu tiên luồng chỉ-tiếng nếu phiên ghi màn hình có lưu.
 * Tải về vẫn là video đầy đủ, chỉ bản upload mới bỏ hình — engine chỉ bóc băng
 * âm thanh, mà phần hình từng làm upload vượt giới hạn 413 của nginx.
 */
const buildUploadBlob = async () => {
    if (!meta.value) return null
    if (meta.value.audioMimeType && (await getChunkCount('audio')) > 0) {
        return assembleBlob(meta.value.audioMimeType, 'audio')
    }
    return assembleBlob(meta.value.mimeType)
}

const onDownload = async () => {
    if (!meta.value || downloading.value) return
    downloading.value = true
    try {
        const blob = await buildBlob()
        if (!blob || blob.size === 0) {
            notification.error({message: 'Lỗi', description: 'Không đọc được dữ liệu bản ghi.'})
            return
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = meta.value.filename || 'recording.webm'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 4000)
        notification.success({message: 'Đã tải về', description: 'File ghi đã được lưu về máy bạn.'})
        await clearSession()
        visible.value = false
    } catch (e) {
        console.error(e)
        notification.error({message: 'Lỗi', description: 'Không tải được file ghi.'})
    } finally {
        downloading.value = false
    }
}

const onUpload = async () => {
    if (!meta.value || uploading.value) return
    uploading.value = true
    try {
        const blob = await buildUploadBlob()
        if (!blob || blob.size === 0) {
            notification.error({message: 'Lỗi', description: 'Không đọc được dữ liệu bản ghi.'})
            return
        }
        const useAudio = !!meta.value.audioMimeType && blob.type === meta.value.audioMimeType
        const file = new File(
            [blob],
            (useAudio ? meta.value.audioFilename : meta.value.filename) || 'recording.webm',
            {type: blob.type || meta.value.mimeType}
        )
        const res: any = await TaskService.uploadTask('meeting', 'vc', null, file)
        if (res?.status) {
            notification.success({message: 'Thành công', description: 'Đã tải bản ghi lên hệ thống.'})
            await clearSession()
            visible.value = false
        } else {
            notification.error({message: 'Lỗi', description: res?.statusText || 'Tải lên thất bại. Bản ghi vẫn được giữ lại.'})
        }
    } catch (e) {
        console.error(e)
        notification.error({message: 'Lỗi', description: 'Không tải lên được. Bản ghi vẫn được giữ lại — bạn có thể tải về máy.'})
    } finally {
        uploading.value = false
    }
}

const onDiscard = () => {
    Modal.confirm({
        title: 'Xoá bản ghi?',
        content: 'Bản ghi tạm sẽ bị xoá vĩnh viễn và không thể khôi phục.',
        okText: 'Xoá',
        okType: 'danger',
        cancelText: 'Huỷ',
        onOk: async () => {
            await clearSession()
            visible.value = false
        },
    })
}
</script>

<style scoped>
.rec-recovery { font-size: 14px; }
.rr-warn { color: #613400; background: #fff7e6; border: 1px solid #ffd591; border-radius: 10px; padding: 12px 14px; line-height: 1.6; margin: 0 0 16px; }
.rr-info { background: #f7f9fc; border: 1px solid #eef1f5; border-radius: 10px; padding: 10px 14px; margin-bottom: 18px; }
.rr-row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; font-size: 13px; }
.rr-row span { color: #8c93a3; }
.rr-file { max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rr-actions { display: flex; flex-direction: column; gap: 10px; }
</style>
